import { useState } from 'react';
import { cbtExercises } from '../data/cbtExercises';
import { reframeThought } from '../utils/reframingLogic';

export default function CBTPage() {
  const [expandedId, setExpandedId] = useState(null);
  const [userThoughts, setUserThoughts] = useState({});
  const [reframedResults, setReframedResults] = useState({});

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleThoughtChange = (id, value) => {
    setUserThoughts({
      ...userThoughts,
      [id]: value,
    });
  };

  const handleReframe = (id, exerciseType) => {
    const thought = userThoughts[id];
    const result = reframeThought(thought, exerciseType);
    
    if (result) {
      setReframedResults({
        ...reframedResults,
        [id]: result,
      });
    }
  };

  const clearExercise = (id) => {
    setUserThoughts({
      ...userThoughts,
      [id]: '',
    });
    setReframedResults({
      ...reframedResults,
      [id]: null,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">🧠 CBT Tools</h1>
          <p className="text-lg text-gray-600">
            Interactive exercises to help you reframe thoughts and manage stress.
          </p>
        </div>

        {/* CBT Information Section */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-12 border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">What is CBT?</h2>
          
          <div className="space-y-4 text-gray-700 mb-6">
            <p>
              <strong>Cognitive Behavioral Therapy (CBT)</strong> is a reflection technique that helps you understand the connection between your thoughts, feelings, and actions.
            </p>
            
            <div className="bg-purple-50 rounded-lg p-4 border-l-4 border-purple-500">
              <p className="font-semibold text-gray-800 mb-2">How it works:</p>
              <ul className="space-y-2 text-sm">
                <li>✓ <strong>Identify:</strong> Notice the thoughts running through your mind</li>
                <li>✓ <strong>Challenge:</strong> Question extreme or unhelpful thinking patterns</li>
                <li>✓ <strong>Replace:</strong> Develop more balanced, realistic perspectives</li>
              </ul>
            </div>

            <p className="text-sm text-gray-600 italic">
              This tool provides general emotional awareness exercises and is <strong>not a substitute for professional mental health care</strong>. If you're struggling, please reach out to a mental health professional.
            </p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>💡 Remember:</strong> These exercises are about awareness and reflection, not fixing yourself. You're already doing great by taking time to understand your thoughts.
            </p>
          </div>
        </div>

        {/* Exercises Grid */}
        <div className="space-y-4">
          {cbtExercises.map((exercise) => (
            <div
              key={exercise.id}
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-200 overflow-hidden"
            >
              {/* Header - Always Visible */}
              <button
                onClick={() => toggleExpand(exercise.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-lg font-bold text-gray-800">{exercise.title}</h2>
                  <p className="text-sm text-gray-600 mt-1">{exercise.description}</p>
                </div>
                <span
                  className={`text-2xl ml-4 transition-transform duration-300 ${
                    expandedId === exercise.id ? 'rotate-180' : ''
                  }`}
                >
                  ▼
                </span>
              </button>

              {/* Content - Expandable */}
              {expandedId === exercise.id && (
                <div className="px-6 py-6 bg-gradient-to-br from-purple-50 to-pink-50 border-t border-gray-200">
                  {/* Instructions */}
                  <div className="mb-6">
                    <h3 className="font-bold text-gray-800 mb-3">Instructions:</h3>
                    <div className="space-y-2">
                      {exercise.instruction.map((step, idx) => (
                        <div key={idx} className="flex gap-3">
                          <span className="text-indigo-600 font-bold flex-shrink-0">{idx + 1}.</span>
                          <p className="text-gray-700 text-sm">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Interactive Section */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <label className="block font-semibold text-gray-800 mb-3">
                      Write the thought bothering you:
                    </label>
                    <textarea
                      value={userThoughts[exercise.id] || ''}
                      onChange={(e) => handleThoughtChange(exercise.id, e.target.value)}
                      placeholder="Example: I always mess everything up..."
                      className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none resize-none bg-gray-50"
                    />

                    {/* Buttons */}
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => handleReframe(exercise.id, exercise.type)}
                        disabled={!userThoughts[exercise.id]?.trim()}
                        className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-2 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Reframe Thought
                      </button>
                      <button
                        onClick={() => clearExercise(exercise.id)}
                        className="px-4 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-all"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Reframed Result */}
                  {reframedResults[exercise.id] && (
                    <div className="mt-6 space-y-4">
                      {/* Original Thought */}
                      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
                        <p className="text-xs font-semibold text-red-700 mb-1">Your Original Thought:</p>
                        <p className="text-gray-800 italic">"{reframedResults[exercise.id].original}"</p>
                      </div>

                      {/* Reframed Thought */}
                      <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-lg">
                        <p className="text-xs font-semibold text-green-700 mb-1">A More Balanced Perspective:</p>
                        <p className="text-gray-800">"{reframedResults[exercise.id].reframed}"</p>
                      </div>

                      {/* Action Suggestion */}
                      <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg">
                        <p className="text-gray-800">{reframedResults[exercise.id].action}</p>
                      </div>

                      {/* Encouragement */}
                      <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-4">
                        <p className="text-sm text-purple-900">
                          💜 You're doing the work. That matters. Even small shifts in perspective count.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer Info */}
        <div className="mt-12 bg-white rounded-xl shadow-md p-6 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-800 mb-3">💡 How to Use These Tools</h3>
          <p className="text-gray-700 text-sm">
            Click any exercise to expand it. Write a thought that's bothering you, then click "Reframe Thought" to see a more balanced perspective. These tools work best with practice and self-compassion.
          </p>
        </div>

        {/* Disclaimer */}
        <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> These are supportive tools for emotional awareness, not therapy. If you need professional help, please reach out to a mental health provider.
          </p>
        </div>
      </div>
    </div>
  );
}
